// Parts kits in the office op panel: the boat's kits (customers/{id}/Boats/{id}/Parts_Kits), a kit's
// parts with on-hand counts, New kit, Add part to kit, and Copy a kit from another boat.
// Split out of app.js (docs/split-plan.md, session 1). The app.js helpers it calls come in through
// setPartsKitDeps() (see estimates.js for why it doesn't import app.js back); adding a kit's parts to
// the job (addPartsKitToJob) stays in app.js with the rest of the parts writes.
import { updateKitPart, updatePartsKit } from './partsKitData';
import { escapeHTML, fetchIdToken, getDB, idbCursorAll, toastMessage, updateTimestamps } from './utilities';

let shell = null;
export function setPartsKitDeps(deps) { shell = deps; }

async function getPartById(id) {
  try {

    const db = await getDB();
    const transaction = db.transaction(['inventoryData'], 'readonly');
    const store = transaction.objectStore('inventoryData');
    
    return new Promise((resolve, reject) => {
      const request = store.get(id);
      request.onsuccess = () => {
        if (request.result) {
          resolve(request.result);
        } else {
          resolve(null); // Part not found
        }
      };
      request.onerror = () => reject(request.error);
    });

  } catch (error) {
    console.error('Error retrieving part:', error);
    throw error;
  }
}

document.getElementById('open-part-kit').addEventListener('click', async (e) => {
  e.stopPropagation();
  const customerId = document.getElementById('updateCustomerId').value;
  const boatId = document.getElementById('updateBoatId').value;

  const customerData = await shell.getCustomerDataById(customerId, boatId).catch(() => null);

  displayPartsKits(customerData?.boatData?.Parts_Kits || {});
});

document.getElementById('create-new-kit').addEventListener('click', async (e) => {
  e.stopPropagation();
  const kitName = await showKitPopup();
  if(!kitName) return;
  displayKitParts('', kitName);
  document.getElementById('currentPartKitName').value = kitName;
});

async function showKitPopup() {

  return new Promise((resolve) => {
    const kitPopup = shell.createPopup('Enter Kit Name');
    kitPopup.className = 'kit-popup';
    const kitInput = document.createElement('input');

    kitInput.type = 'text';
    kitInput.placeholder = 'Kit Name';
    kitInput.className = 'new-kit-input';

    kitInput.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') {
        const kitName = kitInput.value.trim();
        if (kitName !== '') {
          shell.closePopup(kitPopup);
          resolve(kitName);
        }
      }
    });

    kitPopup.appendChild(kitInput);

    const confirmButton = document.createElement('button');
    confirmButton.textContent = 'Submit';
    confirmButton.classList.add('pop-up-submit-button');

    confirmButton.addEventListener('click', () => {
      const kitName = kitInput.value.trim();
      if (kitName !== '') {
        shell.closePopup(kitPopup);

        resolve(kitName);
      }
    });

    const cancelButton = document.createElement('button');
    cancelButton.textContent = 'Cancel';
    cancelButton.classList.add('pop-up-cancel-button');
    cancelButton.addEventListener('click', () => {
      shell.closePopup(kitPopup);
      resolve(null);
    });

    kitPopup.appendChild(confirmButton);
    kitPopup.appendChild(cancelButton);

    document.body.appendChild(kitPopup);
    kitInput.focus();
  });
}

let currentPartsKits = null;

async function displayPartsKits(kits) {
  currentPartsKits = kits || {};
  const kitModal = document.getElementById('kit-select-modal');
  document.getElementById('pk-kit-search').value = '';

  await renderKitList(currentPartsKits, '');

  kitModal.style.display = 'flex';
}

async function renderKitList(kits, query) {
  const kitCollection = document.getElementById('kit-collection');
  kitCollection.innerHTML = '';

  if (!kits) return;

  const q = (query || '').toLowerCase().trim();

  for (const kitName of Object.keys(kits)) {
    if (q && !kitName.toLowerCase().includes(q)) continue;

    const kit = kits[kitName];
    const partNumbers = Object.keys(kit);
    const contents = partNumbers.map(pn => kit[pn].description || pn).slice(0, 4).join(' · ');

    // Stock summary — how many of this kit's lines are short vs. fully in stock.
    let shortCount = 0;
    for (const pn of partNumbers) {
      const info = kit[pn];
      if (!info.id) continue;
      const partData = await getPartById(info.id);
      const onHand = partData ? partsKitOnHand(partData) : 0;
      if (onHand < (Number(info.qty) || 0)) shortCount++;
    }

    const row = document.createElement('button');
    row.type = 'button';
    row.className = 'pk-kit-row';
    row.innerHTML = `
      <div class="pk-kit-icon"><img src="/assets/wrench.png" width="20" height="20" alt=""></div>
      <div class="pk-kit-info">
        <div class="pk-kit-name-row"><span class="pk-kit-name">${escapeHTML(kitName)}</span></div>
        <div class="pk-kit-contents">${escapeHTML(contents)}</div>
      </div>
      <div class="pk-kit-stats">
        <div class="pk-kit-count">${partNumbers.length} part${partNumbers.length === 1 ? '' : 's'}</div>
        <div class="pk-kit-stock ${shortCount ? 'stock-short' : 'stock-ok'}">${shortCount ? shortCount + ' short' : 'all in stock'}</div>
      </div>
    `;
    row.addEventListener('click', () => displayKitParts(kit, kitName));
    kitCollection.appendChild(row);
  }
}

function partsKitOnHand(partData) {
  return Array.isArray(partData.Bins)
    ? partData.Bins.reduce((s, b) => s + (Number(b.qty) || 0), 0)
    : (Number(partData.Qty) || 0);
}

document.getElementById('pk-kit-search').addEventListener('input', (e) => {
  renderKitList(currentPartsKits, e.target.value);
});

function updatePkFooter() {
  const rows = Array.from(document.querySelectorAll('#parts-container .pk-line-row'));
  const total = rows.length;
  const selected = rows.filter(r => r.querySelector('.pk-line-check').checked).length;

  document.getElementById('pk-selected-count').textContent = `${selected} of ${total} selected`;
  document.getElementById('add-to-job').textContent = `Post ${selected} part${selected === 1 ? '' : 's'}`;
  document.getElementById('add-to-job').disabled = selected === 0;

  const shortRows = rows.filter(r => r.querySelector('.pk-line-check').checked && r.dataset.short === '1');
  const banner = document.getElementById('pk-short-banner');
  if (shortRows.length) {
    banner.style.display = '';
    document.getElementById('pk-short-text').textContent =
      `${shortRows.length} line${shortRows.length === 1 ? '' : 's'} short on stock — post it anyway and it lands on the part order.`;
  } else {
    banner.style.display = 'none';
  }
}

// C2 · Review before posting — every kit line gets a checkbox + qty stepper,
// stock shorts (checked against the same inventoryData store the Add Part
// composer reads) get a badge instead of posting blind like before.
async function displayKitParts(kit, kitName) {

  document.getElementById('currentPartKitName').value = kitName;
  // Hide the kit selection modal
  const kitModal = document.getElementById('kit-select-modal');
  kitModal.style.display = 'none';

  // Show the parts modal and update its content
  const partsModal = document.getElementById('kit-parts-modal');
  const partsContainer = document.getElementById('parts-container');
  const kitNameHeader = document.getElementById('kit-name-header');
  partsContainer.innerHTML = ''; // Clear existing parts
  kitNameHeader.textContent = kitName;
  document.getElementById('kit-name-sub').textContent =
    `Posts ${Object.keys(kit).length} rows to Parts under ${document.getElementById('currentTech')?.value || 'you'} · today's date · editable after`;

  // Display each part
  for (const [partNumber, partInfo] of Object.entries(kit)) {
    let partData = { Location: 'N/A' };
    if (partInfo.id) {
      partData = await getPartById(partInfo.id) || partData;
    }

    kit[partNumber].Location = partData.Location;

    const onHand = partsKitOnHand(partData);
    const qtyNeeded = Number(partInfo.qty) || 0;
    const isShort = !!partInfo.id && onHand < qtyNeeded;

    const partDiv = document.createElement('div');
    partDiv.className = 'pk-line-row';
    partDiv.setAttribute('data-part-id', partInfo.id || '');
    partDiv.setAttribute('data-part-number', partNumber);
    partDiv.dataset.short = isShort ? '1' : '0';
    partDiv.innerHTML = `
      <input type="checkbox" class="pk-line-check" checked>
      <div class="pk-line-info">
        <div class="pk-line-num-row">
          <span class="pk-line-num">${escapeHTML(partNumber)}</span>
          ${isShort ? `<span class="part-misc-badge">${onHand} in stock</span>` : ''}
        </div>
        <div class="pk-line-desc">${escapeHTML(partInfo.description || '')}</div>
      </div>
      <div class="pk-qty-stepper">
        <button type="button" class="pk-qty-btn pk-qty-minus">−</button>
        <input type="text" class="pk-qty-value" value="${escapeHTML(String(partInfo.qty ?? ''))}" inputmode="numeric">
        <button type="button" class="pk-qty-btn pk-qty-plus">+</button>
      </div>
    `;

    const qtyInput = partDiv.querySelector('.pk-qty-value');
    const persistQty = async () => {
      partInfo.qty = qtyInput.value;
      // Only this line's quantity: another person may be editing other lines of the same kit.
      await updateKitPart(document.getElementById('updateCustomerId').value, document.getElementById('updateBoatId').value, kitName, partNumber, { qty: qtyInput.value });
    };
    qtyInput.addEventListener('change', persistQty);
    partDiv.querySelector('.pk-qty-minus').addEventListener('click', () => {
      qtyInput.value = Math.max(0, (parseFloat(qtyInput.value) || 0) - 1);
      persistQty();
    });
    partDiv.querySelector('.pk-qty-plus').addEventListener('click', () => {
      qtyInput.value = (parseFloat(qtyInput.value) || 0) + 1;
      persistQty();
    });

    const checkbox = partDiv.querySelector('.pk-line-check');
    checkbox.addEventListener('change', () => {
      partDiv.classList.toggle('excluded', !checkbox.checked);
      updatePkFooter();
    });

    partsContainer.appendChild(partDiv);
  }

  updatePkFooter();

  // Add to Job / Post parts button functionality
  const addToJobBtn = document.getElementById('add-to-job');
  addToJobBtn.onclick = () => {
    const includedParts = Array.from(partsContainer.querySelectorAll('.pk-line-row'))
      .filter(partDiv => partDiv.querySelector('.pk-line-check').checked)
      .map(partDiv => ({
        partNumber: partDiv.getAttribute('data-part-number'),
        qty: partDiv.querySelector('.pk-qty-value').value,
        description: partDiv.querySelector('.pk-line-desc').textContent.trim(),
        id: partDiv.getAttribute('data-part-id')
      }));

    shell.addPartsKitToJob(includedParts);
    partsModal.style.display = 'none';
  };

  // Check for print button and remove if it exists
  if (document.querySelector('.print-kit-button')) {
  document.querySelector('.print-kit-button').remove();
  }

  partsModal.style.display = 'flex';

  const printButton = document.createElement('button');
  printButton.className = 'print-kit-button pk-ghost-button';
  printButton.type = 'button';
  printButton.textContent = 'Print Kit';
  printButton.addEventListener('click', () => printKitPartsTable(kit, kitName));

  document.getElementById('part-kit-buttons').appendChild(printButton);
}

document.getElementById('kit-parts-back').addEventListener('click', () => {
  document.getElementById('kit-parts-modal').style.display = 'none';
  document.getElementById('kit-select-modal').style.display = 'flex';
});

// ---------------------------------------------------------------------------
// Copy Parts Kit from another boat. Kits stay per-boat (customers/{id}/Boats/
// {id}/Parts_Kits) — this just makes it fast to reuse one instead of rebuilding
// it from scratch. Step 1 is a parallel instance of the .wo-picker-* shell
// already used for the WO-creation customer picker (own ids, no shared state
// with that flow). Step 2 reuses Phase 4's .pk-kit-row styling with checkboxes.
// ---------------------------------------------------------------------------

document.getElementById('copy-kit-from-boat').addEventListener('click', () => {
  document.getElementById('kit-select-modal').style.display = 'none';
  openKitCopyPicker();
});

function openKitCopyPicker() {
  document.getElementById('kit-copy-picker-search').value = '';
  document.getElementById('kit-copy-picker-overlay').classList.remove('hidden');
  renderKitCopyPickerResults('');
  document.getElementById('kit-copy-picker-search').focus();
}

document.getElementById('kit-copy-picker-close').addEventListener('click', () => {
  document.getElementById('kit-copy-picker-overlay').classList.add('hidden');
  document.getElementById('kit-select-modal').style.display = 'flex';
});

let kitCopySearchTimer = null;
document.getElementById('kit-copy-picker-search').addEventListener('input', (e) => {
  clearTimeout(kitCopySearchTimer);
  const q = e.target.value.trim().toLowerCase();
  kitCopySearchTimer = setTimeout(() => renderKitCopyPickerResults(q), 150);
});

async function renderKitCopyPickerResults(query) {
  const body = document.getElementById('kit-copy-picker-body');
  const countEl = document.getElementById('kit-copy-picker-count');
  body.innerHTML = '';

  if (!query) {
    countEl.textContent = '';
    body.innerHTML = '<div class="wo-picker-empty">Start typing a customer name&hellip;</div>';
    return;
  }

  let customers;
  try {
    customers = await idbCursorAll('customerData');
  } catch (error) {
    console.error('Error loading customers for kit-copy picker:', error);
    body.innerHTML = '<div class="wo-picker-empty">Could not load customers.</div>';
    return;
  }

  const matches = customers.filter(c => {
    const name = `${c.First_Name || ''} ${c.Last_Name || ''}`.toLowerCase();
    return name.includes(query);
  }).slice(0, 20);

  countEl.textContent = matches.length ? `${matches.length} customer${matches.length === 1 ? '' : 's'}` : '';

  if (!matches.length) {
    body.innerHTML = '<div class="wo-picker-empty">No customers match.</div>';
    return;
  }

  matches.forEach((customer) => {
    const name = customer.Last_Name && customer.First_Name
      ? `${customer.Last_Name}, ${customer.First_Name}`
      : (customer.Last_Name || customer.First_Name || '');
    const initials = ((customer.First_Name || '')[0] || '').toUpperCase() + ((customer.Last_Name || '')[0] || '').toUpperCase();

    const boats = customer.Boats
      ? Object.values(customer.Boats).filter(b => b && typeof b === 'object').sort((a, b) => (a.Boat_Name || '').localeCompare(b.Boat_Name || ''))
      : [];
    // Only boats with at least one kit are worth showing here.
    const boatsWithKits = boats.filter(b => b.Parts_Kits && Object.keys(b.Parts_Kits).length);
    if (!boatsWithKits.length) return;

    const group = document.createElement('div');
    group.className = 'wo-picker-group';
    group.innerHTML = `
      <div class="wo-picker-group-head">
        <div class="wo-picker-group-avatar">${initials || '?'}</div>
        <div class="wo-picker-group-main">
          <div class="wo-picker-group-name">${escapeHTML(name)}</div>
          <div class="wo-picker-group-sub">${escapeHTML(shell.formatPhoneNumber(customer.Phone) || '')}</div>
        </div>
      </div>
      ${boatsWithKits.map(boat => `
        <div class="wo-picker-boat-row" data-boat-id="${escapeHTML(boat.Boat_ID || '')}">
          <img class="wo-picker-boat-icon" src="/assets/boat.png" width="17" height="17" alt="">
          <div class="wo-picker-boat-main">
            <div class="wo-picker-boat-name">${escapeHTML(boat.Boat_Name || '')}</div>
            <div class="wo-picker-boat-sub">${Object.keys(boat.Parts_Kits).length} kit${Object.keys(boat.Parts_Kits).length === 1 ? '' : 's'}</div>
          </div>
        </div>
      `).join('')}
    `;

    group.querySelectorAll('.wo-picker-boat-row[data-boat-id]').forEach((row) => {
      const boat = boatsWithKits.find(b => b.Boat_ID === row.dataset.boatId);
      if (boat) row.addEventListener('click', () => selectKitCopySourceBoat(customer, boat));
    });

    body.appendChild(group);
  });

  if (!body.children.length) {
    body.innerHTML = '<div class="wo-picker-empty">No matching customers have any boats with parts kits.</div>';
  }
}

function selectKitCopySourceBoat(customer, boat) {
  document.getElementById('kit-copy-picker-overlay').classList.add('hidden');

  const customerName = customer.Last_Name && customer.First_Name
    ? `${customer.Last_Name}, ${customer.First_Name}`
    : (customer.Last_Name || customer.First_Name || '');

  document.getElementById('kit-copy-select-source').textContent = `${boat.Boat_Name || ''} · ${customerName}`;
  renderKitCopySourceList(boat.Parts_Kits || {});
  document.getElementById('kit-copy-select-modal').style.display = 'flex';
}

function renderKitCopySourceList(kits) {
  const list = document.getElementById('kit-copy-select-list');
  list.innerHTML = '';

  Object.keys(kits).forEach(kitName => {
    const kit = kits[kitName];
    const partNumbers = Object.keys(kit);
    const contents = partNumbers.map(pn => kit[pn].description || pn).slice(0, 4).join(' · ');

    const row = document.createElement('label');
    row.className = 'pk-kit-row';
    row.innerHTML = `
      <input type="checkbox" class="pk-line-check" data-kit-name="${escapeHTML(kitName)}">
      <div class="pk-kit-icon"><img src="/assets/wrench.png" width="20" height="20" alt=""></div>
      <div class="pk-kit-info">
        <div class="pk-kit-name-row"><span class="pk-kit-name">${escapeHTML(kitName)}</span></div>
        <div class="pk-kit-contents">${escapeHTML(contents)}</div>
      </div>
      <div class="pk-kit-stats"><div class="pk-kit-count">${partNumbers.length} part${partNumbers.length === 1 ? '' : 's'}</div></div>
    `;
    row.querySelector('.pk-line-check').addEventListener('change', updateKitCopySelectFooter);
    row.dataset.kitData = JSON.stringify(kit);
    list.appendChild(row);
  });

  updateKitCopySelectFooter();
}

function updateKitCopySelectFooter() {
  const checks = Array.from(document.querySelectorAll('#kit-copy-select-list .pk-line-check'));
  const selected = checks.filter(c => c.checked).length;
  document.getElementById('kit-copy-select-count').textContent = `${selected} of ${checks.length} selected`;
  const postBtn = document.getElementById('kit-copy-select-post');
  postBtn.textContent = `Copy ${selected} kit${selected === 1 ? '' : 's'}`;
  postBtn.disabled = selected === 0;
}

document.getElementById('kit-copy-select-back').addEventListener('click', () => {
  document.getElementById('kit-copy-select-modal').style.display = 'none';
  openKitCopyPicker();
});

document.getElementById('kit-copy-select-modal').querySelector('[data-close="kit-copy-select-modal"]').addEventListener('click', () => {
  document.getElementById('kit-select-modal').style.display = 'flex';
});

// A kit with this exact name may already exist on the destination boat —
// updatePartsKit() PATCHes by name, so silently reusing that name would merge
// into (and could overwrite fields on) whatever's already there. Suffix instead.
function uniqueKitName(existingKits, desiredName) {
  if (!existingKits || !existingKits[desiredName]) return desiredName;
  let candidate = `${desiredName} (copied)`;
  let n = 2;
  while (existingKits[candidate]) {
    candidate = `${desiredName} (copied ${n})`;
    n++;
  }
  return candidate;
}

document.getElementById('kit-copy-select-post').addEventListener('click', async () => {
  const checked = Array.from(document.querySelectorAll('#kit-copy-select-list .pk-line-check:checked'));
  if (!checked.length) return;

  const destCustomerId = document.getElementById('updateCustomerId').value;
  const destBoatId = document.getElementById('updateBoatId').value;

  const destCustomerData = await shell.getCustomerDataById(destCustomerId, destBoatId);
  const destKits = destCustomerData.boatData.Parts_Kits || {};

  for (const checkbox of checked) {
    const row = checkbox.closest('.pk-kit-row');
    const sourceName = checkbox.dataset.kitName;
    const kitData = JSON.parse(row.dataset.kitData);
    const newName = uniqueKitName(destKits, sourceName);
    destKits[newName] = kitData;
    await updatePartsKit(destCustomerId, destBoatId, newName, kitData);
  }

  toastMessage(`Copied ${checked.length} kit${checked.length === 1 ? '' : 's'}`, 'green');

  document.getElementById('kit-copy-select-modal').style.display = 'none';

  // Refresh the main picker so the copied kit(s) show up immediately.
  const refreshed = await shell.getCustomerDataById(destCustomerId, destBoatId);
  await displayPartsKits(refreshed.boatData.Parts_Kits);
});

function printKitPartsTable(kit, kitName) {
  console.log('Printing kit:', kitName, kit);
  // Create a new window for printing
  const printWindow = window.open('', '_blank');
  
  // Sort the kit parts by Location
  const sortedKitParts = Object.entries(kit).sort((a, b) => {
    const locationA = a[1].Location || '';
    const locationB = b[1].Location || '';
    return locationB.localeCompare(locationA);
  });

  // Start building the HTML content
  let htmlContent = `
    <html>
    <head>
      <title>${kitName} Parts Kit</title>
      <style>
        body { font-family: Arial, sans-serif; }
        table { width: 100%; border-collapse: collapse; }
        th, td { border: 1px solid black; padding: 8px; text-align: left; }
        th { background-color: #f2f2f2; }
        .part-description { font-size: 0.8em; color: #666; }
      </style>
    </head>
    <body>
      <h1>${kitName} Parts Kit</h1>
      <table>
        <thead>
          <tr>
            <th style="text-align: center; vertical-align: middle; font-size: 1.25rem; background-color:darkgray; color:white;">Part Number</th>
            <th style="text-align: center; vertical-align: middle; font-size: 1.25rem; background-color:darkgray; color:white;">Description</th>
            <th style="text-align: center; vertical-align: middle; font-size: 1.25rem; background-color:darkgray; color:white;">Quantity</th>
            <th style="text-align: center; vertical-align: middle; font-size: 1.25rem; background-color:darkgray; color:white;">Location</th>
            <th style="text-align: center; vertical-align: middle; font-size: 1.25rem; background-color:darkgray; color:white;">Included</th>
          </tr>
        </thead>
        <tbody>
  `;

  // Add each part to the table
  sortedKitParts.forEach(([partNumber, partInfo]) => {
    htmlContent += `
      <tr>
        <td style="text-align: center; vertical-align: middle;">${escapeHTML(partNumber)}</td>
        <td style="text-align: center; vertical-align: middle;">
          <br>
          <span>${escapeHTML(partInfo.description || '')}</span>
        </td>
        <td style="text-align: center; vertical-align: middle;">${escapeHTML(String(partInfo.qty ?? ''))}</td>
        <td style="text-align: center; vertical-align: middle;">${escapeHTML(partInfo.Location || '')}</td>
        <td style="text-align: center; vertical-align: middle;"><input type="checkbox" style="width: 30px; height: 30px; text-align:center;"></td>
      </tr>
    `;
  });

  // Close the table and HTML structure
  htmlContent += `
        </tbody>
      </table>
    </body>
    </html>
  `;

  // Write the content to the new window and print
  printWindow.document.write(htmlContent);
  printWindow.document.close();
  printWindow.print();
}

document.getElementById('add-part-to-kit').addEventListener('click', async (e) => {
  e.stopPropagation();
  document.getElementById('partSelectModal').style.display = 'flex';
});

export async function addPartToPartsKit(partData, kitName) {
  // Retrieve the customer and boat IDs from the DOM
  const customerId = document.getElementById('updateCustomerId').value;
  const boatId = document.getElementById('updateBoatId').value;

  // Fetch the current customer data
  const customerData = await shell.getCustomerDataById(customerId, boatId).catch(() => null);

  // Kits are saved on the boat record — without one there's nowhere to put it.
  if (!customerData?.boatData) {
    toastMessage('This operation has no boat record on file, so a parts kit cannot be saved for it.', 'red');
    return;
  }

  if(!customerData.boatData.Parts_Kits){
    customerData.boatData.Parts_Kits = {};
  }

  let partsKits = customerData.boatData.Parts_Kits;


  if (!partsKits[kitName]) {
    partsKits[kitName] = {};
  }

  const data = await showKitQuantityPopup(partData.Part_Number, partData.Description);
  if (!data) return; // Cancel

  const partNumber = partData.Part_Number;
  partsKits[kitName][partNumber] = {
    partNumber: partData.Part_Number,
    description: partData.Description ? partData.Description : data.New_Description,
    qty: data.Qty,
    id: partData.id,
  };

  // This line only: the kit on this device may be missing lines someone else added.
  await updateKitPart(customerId, boatId, kitName, partNumber, partsKits[kitName][partNumber]);

  displayKitParts(partsKits[kitName], kitName)
}

async function showKitQuantityPopup(partNumber, description) {
  // If the description is blank, ask the user for it
  if (!description || description.trim() === '') {
    description = await shell.showDescriptionPopup(partNumber);
  }

  return new Promise((resolve) => {
    const qtyPopup = shell.createPopup('Enter Quantity');
    const qtyInput = document.createElement('input');

    qtyInput.type = 'number';
    qtyInput.placeholder = 'Quantity';
    qtyInput.className = 'new-qty-input';

    qtyInput.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') {
        const quantity = qtyInput.value.trim();
        if (quantity !== '') {
          shell.closePopup(qtyPopup);
          const data = {
            "Qty": quantity,
            "New_Description": description
          };
          resolve(data); // Resolve the Promise with the data
        }
      }
    });

    qtyPopup.appendChild(qtyInput);

    const confirmButton = document.createElement('button');
    confirmButton.textContent = 'Submit';
    confirmButton.classList.add('pop-up-submit-button')

    confirmButton.addEventListener('click', () => {
      const quantity = qtyInput.value.trim();
      if (quantity !== '') {
        shell.closePopup(qtyPopup);
        const data = {
          "Qty": quantity,
          "New_Description": description
        };
        resolve(data);
      }
    });

    const cancelButton = document.createElement('button');
    cancelButton.textContent = 'Cancel';
    cancelButton.classList.add('pop-up-cancel-button');
    cancelButton.addEventListener('click', () => {
      shell.closePopup(qtyPopup);
      resolve(null);
    });

    qtyPopup.appendChild(confirmButton);
    qtyPopup.appendChild(cancelButton);

    document.body.appendChild(qtyPopup);
    qtyInput.focus();
  });
}

